import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError } from '@/features/auth/api';
import {
  CONTENT_VERSION,
  createDraft,
  EMPTY_EVALUATION,
  INVOICE_DRAFTS_QUERY_KEY,
  saveDraft,
  validateDraftContent,
  type DraftContent,
  type DraftDetail,
  type DraftEvaluation,
  type TaxScheme,
} from './api';
import { isDraftContentBlank } from './content';

/** How long after the last edit to run the (lock-free) live validation. */
const VALIDATE_DEBOUNCE_MS = 400;
/** How long after the last edit to persist (version-locked autosave). Longer than validate. */
const AUTOSAVE_DEBOUNCE_MS = 1200;

export interface DraftAutosave {
  /** The evaluation to render — seeded from the initial load, then driven by the live `validate` loop. */
  evaluation: DraftEvaluation;
  /**
   * The line keys, in order, of the content that produced the current [evaluation] — captured at the
   * moment that content was sent to validate. Lets the composer map the evaluation's 1-based line
   * indices onto stable line keys, so a drag-reorder keeps per-line errors on the right row even
   * before the next validation lands.
   */
  evaluationLineKeys: number[];
  /**
   * The additional-data row keys, in order, of the content that produced the current [evaluation] —
   * captured when that content was sent to validate, so the composer can map the evaluation's 1-based
   * additional-data indices onto stable row keys the same way [evaluationLineKeys] does for lines.
   */
  evaluationAdditionalDataKeys: number[];
  /** True once a `VERSION_CONFLICT` was hit: autosave halts, validation keeps running, reload required. */
  conflict: boolean;
  /** True when the draft is no longer editable (status left ACTIVE, or `DRAFT_NOT_EDITABLE`). */
  readOnly: boolean;
  /** True while a persist (or the lazy create) is in flight (for a "saving…/saved" indicator). */
  saving: boolean;
  /** Persist any pending edits right now and resolve once saved — used before preview and on unmount. */
  flush: () => Promise<void>;
  /**
   * Permanently stop this draft's loops — cancels pending timers and suppresses the unmount flush and
   * any further create/save. Used when the row is being discarded (deleted): without it, the
   * navigate-away unmount flush would PUT (or lazily recreate) the very draft we just deleted.
   */
  stop: () => void;
}

/**
 * Runs the composer's request loops against a single draft — including bringing that draft into
 * existence.
 *
 * **Lazy create.** When `initial` is null the composer is editing a not-yet-created ("new") invoice: no
 * server row exists and nothing is validated or saved. The first *meaningful* edit (a linked buyer, or
 * any non-blank field) POSTs a fresh draft, persists the typed content, and hands the new id back via
 * `onCreated` — so the composer only ever creates a row the user actually started, and `/invoices/new/standard`
 * never leaves an abandoned blank draft behind. Content typed while the create is in flight isn't lost:
 * it lives in local editor state and rides along on the first save.
 *
 * Once a row exists (either `initial` was provided, or the lazy create landed) two loops drive it:
 *
 * - **validate** (short debounce, lock-free): POSTs the live editor content on every change and drives
 *   the evaluation panel. Never 409s, so feedback keeps flowing even after a conflict. A sequence guard
 *   drops stale responses so the panel always reflects the latest content.
 * - **autosave** (longer debounce, version-locked): PUTs the content, adopting the returned `version`
 *   for the next save. A 409 `VERSION_CONFLICT` latches `conflict` (halting further saves);
 *   `DRAFT_NOT_EDITABLE` latches `readOnly`. Transient failures leave the draft dirty to retry.
 *
 * `flush()` cancels pending timers, creates the row if a meaningful edit is still uncommitted, waits out
 * any in-flight save, and persists once more if dirty — the caller awaits it before rendering the
 * server-side preview (which reads the *saved* row) and it also fires on unmount so navigating away
 * doesn't drop the last edits.
 */
export function useDraftAutosave(params: {
  content: DraftContent;
  customerId: number | null;
  /** The draft's tax scheme — carried into the lazy create so the row is born with the right one. */
  taxScheme: TaxScheme;
  /** The loaded draft when editing an existing row; null for a not-yet-created ("new") invoice. */
  initial: DraftDetail | null;
  /** Current editor line keys, in order — kept in sync with `content.lines` for error re-mapping. */
  lineKeys: number[];
  /** Current additional-data row keys, in order — kept in sync with `content.additionalData`. */
  additionalDataKeys: number[];
  /** Fired once when the lazy create lands, with the new draft id — the caller adopts it (URL swap). */
  onCreated?: (id: number) => void;
  /** Fired if the lazy create fails — the caller surfaces it; the next edit retries. */
  onCreateFailed?: () => void;
}): DraftAutosave {
  const {
    content,
    customerId,
    taxScheme,
    initial,
    lineKeys,
    additionalDataKeys,
    onCreated,
    onCreateFailed,
  } = params;
  const qc = useQueryClient();

  const [evaluation, setEvaluation] = useState<DraftEvaluation>(
    initial?.evaluation ?? EMPTY_EVALUATION,
  );
  const [evaluationLineKeys, setEvaluationLineKeys] = useState<number[]>(lineKeys);
  const [evaluationAdditionalDataKeys, setEvaluationAdditionalDataKeys] =
    useState<number[]>(additionalDataKeys);
  const [conflict, setConflict] = useState(false);
  const [readOnly, setReadOnly] = useState(
    initial != null && initial.draft.status !== 'ACTIVE',
  );
  const [saving, setSaving] = useState(false);

  // Latest values, read by the timer-driven callbacks (the "latest value ref" pattern).
  const contentRef = useRef(content);
  contentRef.current = content;
  const customerIdRef = useRef(customerId);
  customerIdRef.current = customerId;
  const taxSchemeRef = useRef(taxScheme);
  taxSchemeRef.current = taxScheme;
  const lineKeysRef = useRef(lineKeys);
  lineKeysRef.current = lineKeys;
  const additionalDataKeysRef = useRef(additionalDataKeys);
  additionalDataKeysRef.current = additionalDataKeys;
  const onCreatedRef = useRef(onCreated);
  onCreatedRef.current = onCreated;
  const onCreateFailedRef = useRef(onCreateFailed);
  onCreateFailedRef.current = onCreateFailed;

  // The draft id, once it exists. Null until the lazy create lands; every loop below no-ops while null.
  const idRef = useRef<number | null>(initial?.draft.id ?? null);
  const versionRef = useRef(initial?.draft.version ?? 0);
  // Guards the one-shot lazy create: true once a row exists (given up front, or created here).
  const createStartedRef = useRef(initial != null);
  const dirtyRef = useRef(false);
  const savingRef = useRef(false);
  const savePendingRef = useRef(false);
  const savingPromiseRef = useRef<Promise<void> | null>(null);
  const validateSeqRef = useRef(0);
  const conflictRef = useRef(false);
  const readOnlyRef = useRef(initial != null && initial.draft.status !== 'ACTIVE');
  const mountedRef = useRef(true);
  // Latched by stop() when the draft is being discarded — gates every create/save path below.
  const discardedRef = useRef(false);
  const validateTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const skipRef = useRef(true);

  const runValidate = useCallback(async () => {
    if (idRef.current == null) return;
    const seq = ++validateSeqRef.current;
    // Snapshot the row order sent with this request so the result maps back to the right rows.
    const keysAtSend = lineKeysRef.current;
    const additionalKeysAtSend = additionalDataKeysRef.current;
    try {
      const next = await validateDraftContent(idRef.current, {
        customerId: customerIdRef.current,
        content: contentRef.current,
        contentVersion: CONTENT_VERSION,
      });
      if (mountedRef.current && seq === validateSeqRef.current) {
        setEvaluation(next);
        setEvaluationLineKeys(keysAtSend);
        setEvaluationAdditionalDataKeys(additionalKeysAtSend);
      }
    } catch {
      // Validation is best-effort feedback; keep the last evaluation on failure.
    }
  }, []);

  const runSaveRef = useRef<(() => Promise<void>) | undefined>(undefined);
  const runSave = useCallback(async () => {
    const id = idRef.current;
    if (
      id == null ||
      conflictRef.current ||
      readOnlyRef.current ||
      discardedRef.current ||
      !dirtyRef.current
    )
      return;
    if (savingRef.current) {
      savePendingRef.current = true;
      return;
    }
    savingRef.current = true;
    dirtyRef.current = false;
    if (mountedRef.current) setSaving(true);

    const promise = (async () => {
      try {
        const detail = await saveDraft(id, {
          expectedVersion: versionRef.current,
          customerId: customerIdRef.current,
          content: contentRef.current,
          contentVersion: CONTENT_VERSION,
        });
        versionRef.current = detail.draft.version;
        // Keep the cached detail authoritative. `useDraft` never refetches (staleTime: Infinity), so
        // if we left the stale row in cache, switching away and back to this draft would reseed the
        // editor with the *old* version and the next autosave would 409 with a spurious
        // "changed in another tab" conflict.
        qc.setQueryData([...INVOICE_DRAFTS_QUERY_KEY, 'detail', id], detail);
        void qc.invalidateQueries({ queryKey: [...INVOICE_DRAFTS_QUERY_KEY, 'list'] });
      } catch (e) {
        dirtyRef.current = true; // not persisted — a later edit or flush retries
        if (e instanceof ApiError && e.status === 409) {
          if (e.code === 'VERSION_CONFLICT') {
            conflictRef.current = true;
            if (mountedRef.current) setConflict(true);
          } else if (e.code === 'DRAFT_NOT_EDITABLE') {
            readOnlyRef.current = true;
            if (mountedRef.current) setReadOnly(true);
          }
        }
      }
    })();
    savingPromiseRef.current = promise;
    await promise;
    savingPromiseRef.current = null;
    savingRef.current = false;
    if (mountedRef.current) setSaving(false);

    if (savePendingRef.current && !conflictRef.current && !readOnlyRef.current) {
      savePendingRef.current = false;
      void runSaveRef.current?.();
    }
  }, [qc]);
  runSaveRef.current = runSave;

  // Bring the draft into existence on the first meaningful edit. POSTs a fresh row (pre-linking the
  // buyer when the trigger was a customer pick), then persists the typed content so the server holds it
  // before we hand the id back — the create-in-flight guard holds off a *second* create, not the typing.
  const ensureCreated = useCallback(async () => {
    if (idRef.current != null || createStartedRef.current || discardedRef.current) return;
    createStartedRef.current = true;
    if (mountedRef.current) setSaving(true);
    try {
      const cust = customerIdRef.current;
      const created = await createDraft(cust, taxSchemeRef.current);
      idRef.current = created.id;
      versionRef.current = created.version;
      dirtyRef.current = true; // the edit that triggered the create is not yet persisted
      // Persist now (carries the latest typed content) and refresh the evaluation panel, *then* hand
      // the id up — so the row already holds the content whether or not adopting the id remounts us.
      await runSaveRef.current?.();
      void runValidate();
      onCreatedRef.current?.(created.id);
    } catch {
      createStartedRef.current = false; // let the next edit (or flush) retry the create
      if (mountedRef.current) setSaving(false);
      onCreateFailedRef.current?.();
    }
  }, [runValidate]);

  const flush = useCallback(async () => {
    if (discardedRef.current) return;
    clearTimeout(validateTimer.current);
    clearTimeout(saveTimer.current);
    // A meaningful edit may still be uncommitted (create not started, or in flight) — settle it first.
    if (idRef.current == null) {
      const meaningful =
        customerIdRef.current != null || !isDraftContentBlank(contentRef.current);
      if (createStartedRef.current || meaningful) await ensureCreated();
    }
    if (savingPromiseRef.current) await savingPromiseRef.current.catch(() => {});
    if (!conflictRef.current && !readOnlyRef.current && dirtyRef.current) {
      await runSaveRef.current?.();
    }
  }, [ensureCreated]);

  const stop = useCallback(() => {
    discardedRef.current = true;
    clearTimeout(validateTimer.current);
    clearTimeout(saveTimer.current);
  }, []);

  // Whenever the composed content (or linked buyer) changes, either bring the draft into existence (on
  // the first meaningful edit) or restart both debounce timers against the existing row.
  useEffect(() => {
    if (skipRef.current) {
      skipRef.current = false;
      return;
    }
    if (idRef.current == null) {
      if (customerIdRef.current != null || !isDraftContentBlank(contentRef.current)) {
        void ensureCreated();
      }
      return;
    }
    dirtyRef.current = true;
    clearTimeout(validateTimer.current);
    validateTimer.current = setTimeout(() => void runValidate(), VALIDATE_DEBOUNCE_MS);
    if (!conflictRef.current && !readOnlyRef.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => void runSave(), AUTOSAVE_DEBOUNCE_MS);
    }
  }, [content, customerId, runValidate, runSave, ensureCreated]);

  // On a real unmount (navigate-away), best-effort persist the last edits. State setters are
  // mount-guarded, and the save fetch runs to completion regardless — so nothing is dropped. A create
  // already in flight likewise finishes its POST → save chain even after we unmount.
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(validateTimer.current);
      clearTimeout(saveTimer.current);
      if (
        idRef.current != null &&
        dirtyRef.current &&
        !conflictRef.current &&
        !readOnlyRef.current &&
        !discardedRef.current
      ) {
        void runSaveRef.current?.();
      }
    };
  }, []);

  return {
    evaluation,
    evaluationLineKeys,
    evaluationAdditionalDataKeys,
    conflict,
    readOnly,
    saving,
    flush,
    stop,
  };
}
